import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, DollarSign, Gift, Share2, Copy, Mail, CheckCircle2, Clock, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function ReferralsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total_referrals: 0,
    active_referrals: 0,
    completed_referrals: 0,
    total_earnings: 0,
    pending_earnings: 0,
    referral_code: ''
  });
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [bonusAmount, setBonusAmount] = useState(50);

  useEffect(() => {
    if (user) {
      fetchReferralData();
    }
  }, [user]);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch bonus amount from settings
      try {
        const bonusResponse = await axios.get(`${backendUrl}/api/settings/public/referral_bonus_amount`);
        setBonusAmount(bonusResponse.data.value || 50);
      } catch (e) {
        // Use default if settings not available
        setBonusAmount(50);
      }

      // Fetch stats
      const statsResponse = await axios.get(`${backendUrl}/api/referrals/my-stats`, { headers });
      setStats(statsResponse.data);

      // Fetch referral list
      const referralsResponse = await axios.get(`${backendUrl}/api/referrals/my-referrals`, { headers });
      setReferrals(referralsResponse.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(stats.referral_code);
    toast.success('Referral code copied to clipboard!');
  };

  const handleCopyLink = () => {
    const referralLink = `${window.location.origin}/login?ref=${stats.referral_code}`;
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSending(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      await axios.post(
        `${backendUrl}/api/referrals/create`,
        { email: inviteEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Invitation sent successfully!');
      setInviteEmail('');
      setShowInviteDialog(false);
      fetchReferralData();
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="h-5 w-5 text-success" />;
      case 'active': case 'pending': return <Clock className="h-5 w-5 text-warning" />;
      default: return <XCircle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Referrals" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Referrals" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Referral Program</h2>
          <p className="text-muted-foreground">Earn rewards by referring friends</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_referrals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{stats.completed_referrals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Earnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.total_earnings.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-warning">${stats.pending_earnings.toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Referral Code */}
        <Card className="bg-gradient-to-br from-primary/10 to-secondary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5" />
              Your Referral Code
            </CardTitle>
            <CardDescription>Share this code with friends to earn ${bonusAmount} per referral</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 p-4 bg-background rounded-lg border-2 border-dashed">
                <p className="text-2xl font-bold text-center">{stats.referral_code}</p>
              </div>
              <Button onClick={handleCopyCode}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Code
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCopyLink} variant="outline" className="flex-1">
                <Share2 className="mr-2 h-4 w-4" />
                Copy Link
              </Button>
              <Button onClick={() => setShowInviteDialog(true)} className="flex-1">
                <Mail className="mr-2 h-4 w-4" />
                Invite by Email
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referral List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Referrals</CardTitle>
            <CardDescription>Track your referral status and earnings</CardDescription>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No referrals yet</p>
                <p className="text-sm text-muted-foreground">Start inviting friends to earn rewards!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(referral.status)}
                      <div>
                        <p className="font-medium">{referral.referred_name}</p>
                        <p className="text-sm text-muted-foreground">{referral.referred_email}</p>
                        <p className="text-xs text-muted-foreground">
                          Referred on {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${referral.reward_amount.toFixed(2)}</p>
                      <Badge variant={referral.status === 'completed' ? 'default' : 'secondary'}>
                        {referral.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold">1</div>
                <div>
                  <p className="font-medium">Share Your Code</p>
                  <p className="text-sm text-muted-foreground">Send your unique referral code to friends</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold">2</div>
                <div>
                  <p className="font-medium">They Sign Up</p>
                  <p className="text-sm text-muted-foreground">Your friend creates an account using your code</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold">3</div>
                <div>
                  <p className="font-medium">You Both Earn</p>
                  <p className="text-sm text-muted-foreground">Get ${bonusAmount} credit when they complete their first filing</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite by Email</DialogTitle>
              <DialogDescription>Send a referral invitation to your friend</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="friend@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
              <Button onClick={handleSendInvite} disabled={sending}>
                {sending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
